import {
  FLASH_SALE_CAMPAIGN,
  isOfferConfigured,
  getStandardPrice,
  getRenewalPrice,
  getDiscountPercent,
  getSavingsAmount,
  formatOfferPrice,
  getBillingSuffix,
  getPlanForCampaign,
} from './campaignConfig';

const CAMPAIGN_STORAGE_KEY = 'pt_flash_sale_campaign';
const CHECKOUT_FLAG_KEY = 'pt_flash_sale_checkout';

export function parseCampaignDate(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * @param {object} [campaign]
 * @param {Date|number} [now]
 * @returns {'inactive'|'scheduled'|'active'|'expired'}
 */
export function getCampaignStatus(campaign = FLASH_SALE_CAMPAIGN, now = Date.now()) {
  if (!isOfferConfigured(campaign)) return 'inactive';

  const t = typeof now === 'number' ? now : now.getTime();
  const start = parseCampaignDate(campaign.startsAt);
  const end = parseCampaignDate(campaign.endsAt);
  if (!end) return 'inactive';

  if (start && t < start.getTime()) return 'scheduled';
  if (t >= end.getTime()) return 'expired';
  return 'active';
}

export function isCampaignPurchasable(campaign = FLASH_SALE_CAMPAIGN, now = Date.now()) {
  return getCampaignStatus(campaign, now) === 'active';
}

/**
 * Fixed-deadline countdown. Never negative; zeros when expired.
 * @returns {{ days: number, hours: number, minutes: number, seconds: number, totalMs: number, expired: boolean }}
 */
export function getCountdownParts(endsAt, now = Date.now()) {
  const end = parseCampaignDate(endsAt);
  if (!end) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, totalMs: 0, expired: true };
  }
  const t = typeof now === 'number' ? now : now.getTime();
  const totalMs = Math.max(0, end.getTime() - t);
  const expired = totalMs <= 0;
  const totalSeconds = Math.floor(totalMs / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return { days, hours, minutes, seconds, totalMs, expired };
}

export function getOfferSummary(campaign = FLASH_SALE_CAMPAIGN, now = Date.now()) {
  const status = getCampaignStatus(campaign, now);
  const plan = getPlanForCampaign(campaign);
  const standardPrice = getStandardPrice(campaign);
  const promotionalPrice = campaign.offer?.promotionalPrice;
  const renewalPrice = getRenewalPrice(campaign);
  const discountPercent = getDiscountPercent(campaign);
  const savings = getSavingsAmount(campaign);
  const suffix = getBillingSuffix(campaign);
  const discountScope = campaign.offer?.discountScope || 'first_year';

  return {
    status,
    purchasable: status === 'active',
    plan,
    planTier: campaign.offer?.planTier,
    billingInterval: campaign.offer?.billingInterval,
    standardPrice,
    promotionalPrice,
    renewalPrice,
    discountPercent,
    savings,
    suffix,
    discountScope,
    endsAt: campaign.endsAt,
    standardDisplay: formatOfferPrice(standardPrice),
    promoDisplay: promotionalPrice != null ? formatOfferPrice(promotionalPrice) : null,
    renewalDisplay: formatOfferPrice(renewalPrice),
    savingsDisplay: savings != null ? formatOfferPrice(savings) : null,
  };
}

export function persistCampaignAttribution(campaign = FLASH_SALE_CAMPAIGN) {
  try {
    sessionStorage.setItem(
      CAMPAIGN_STORAGE_KEY,
      JSON.stringify({
        id: campaign.id,
        name: campaign.name,
        savedAt: new Date().toISOString(),
      })
    );
  } catch (_) {}
}

export function readPersistedCampaignId() {
  try {
    const raw = sessionStorage.getItem(CAMPAIGN_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw)?.id || null;
  } catch (_) {
    return null;
  }
}

export function markCheckoutIntent() {
  try {
    sessionStorage.setItem(CHECKOUT_FLAG_KEY, '1');
  } catch (_) {}
}

export function consumeCheckoutIntent() {
  try {
    const v = sessionStorage.getItem(CHECKOUT_FLAG_KEY);
    sessionStorage.removeItem(CHECKOUT_FLAG_KEY);
    return v === '1';
  } catch (_) {
    return false;
  }
}

export function buildRegisterUrl(campaign = FLASH_SALE_CAMPAIGN) {
  const redirect = campaign.cta?.registerRedirect || '/flash-sale?checkout=1';
  const params = new URLSearchParams();
  params.set('redirect', redirect);
  params.set('campaign', campaign.id);
  return `/register?${params.toString()}`;
}

export function buildExpiredFallbackUrl(campaign = FLASH_SALE_CAMPAIGN) {
  return campaign.cta?.expiredFallbackPath || '/pricing';
}

/** Renewal disclosure copy based on discount scope. */
export function getRenewalDisclosure(summary) {
  if (!summary?.purchasable && summary?.status !== 'active') return null;
  const { discountScope, renewalDisplay, suffix, promoDisplay } = summary;
  if (discountScope === 'recurring') {
    return `${promoDisplay}${suffix} while the promo lasts. Cancel anytime.`;
  }
  if (discountScope === 'first_period') {
    return `First billing period ${promoDisplay}${suffix}, then ${renewalDisplay}${suffix}. Cancel anytime.`;
  }
  // first_year (default for flash sale)
  return `${promoDisplay}${suffix} for the first year, then ${renewalDisplay}${suffix}. Cancel anytime.`;
}
